import { isEqual, parse } from 'date-fns'
import { fromJS } from 'immutable'
import { get } from 'lodash-es'
import { change } from 'redux-form/immutable'

import { isLeidosUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { getAge } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'

import { LEIDOS_FORM_FIELDS, PAYMENT_FORM, ROLES } from '../../../constants/appConstants'
import { hasRole } from '../../../utils/utils'
import validation, { validateDateOfBirth } from '../../../utils/validation'

const requestPaymentDOB = ({ $$values, field, lyraCodeInfo, FORM_NAME, dispatch, $$formMeta, user }: any) => {
  const errors: Record<string, string | undefined> = {}
  const enteredDob = $$values.get(field.fieldName)
  const lyraCodeDOB = lyraCodeInfo?.user_info?.dob
  const isDependent = $$values.get('relationship') === 'dependent'
  // Payment admins should not get an error for non matching lyra code date of birth
  const shouldThrowLyraCodeError = !hasRole(user.roles, ROLES.PAYMENTS_ADMIN)

  if (!enteredDob) {
    errors[field.fieldName] = `${field.errorText} is required`
  } else {
    const { isValid, message } = validateDateOfBirth(enteredDob, { min: 1, max: 150 })
    if (!isValid) {
      errors[field.fieldName] = message
    } else if (lyraCodeDOB) {
      const enteredAge = getAge({ date: enteredDob })
      if (isEqual(parse(enteredDob, 'MM/dd/yyyy', new Date()), parse(lyraCodeDOB, 'MM-dd-yyyy', new Date()))) {
        if (
          !$$formMeta.getIn([PAYMENT_FORM.FIELDS.FIRST_NAME, 'visited']) &&
          !$$formMeta.getIn([PAYMENT_FORM.FIELDS.LAST_NAME, 'visited'])
        ) {
          /*
          Dispatching form change in validation is not ideal. The alternative is to put this logic in componentDidUpdate of
          RequestPayment.js but without upgrading to redux-form v8 there is no way to manually throw a synchronous error
          from there. This was a compromise in order to keep all of the business logic together.
          */
          dispatch(change(FORM_NAME, PAYMENT_FORM.FIELDS.FIRST_NAME, lyraCodeInfo.user_info.first_name))
          dispatch(change(FORM_NAME, PAYMENT_FORM.FIELDS.LAST_NAME, lyraCodeInfo.user_info.last_name))
        }
      } else if (shouldThrowLyraCodeError && (!isDependent || enteredAge >= 18)) {
        errors[field.fieldName] = `${field.errorText} Does not correspond with Lyra code`
      }
    }
  }
  return errors
}

const requestPaymentValidate = ($$values = fromJS({}), props: any) => {
  let errors = {}
  const companyRaw = $$values.get('company')
  const companyArray = companyRaw?.toJS?.() ?? companyRaw
  const customerName = companyArray?.[0]?.value
  const isLeidos = isLeidosUser(customerName)

  const requiredForGuaranteedTime = [
    {
      fieldName: 'company',
      errorText: 'Sponsoring company',
    },
    {
      fieldName: 'session_type',
      errorText: 'Session type',
    },
    {
      fieldName: 'visit_date',
      errorText: 'Visit date',
    },
    {
      fieldName: 'provider_lyra_id',
      errorText: 'Provider',
    },
  ]

  const restRequired = [
    {
      fieldName: 'diagnosis',
      errorText: 'Diagnosis',
    },
    {
      fieldName: 'eligible_member_first_name',
      errorText: 'Eligible member first name',
    },
    {
      fieldName: 'eligible_member_last_name',
      errorText: 'Eligible member last name',
    },
    {
      fieldName: 'relationship',
      errorText: 'Relationship',
    },
    {
      fieldName: 'other_diagnosis',
      errorText: 'Other diagnosis',
    },
    {
      fieldName: 'other_session_description',
      errorText: 'Session description',
    },
    {
      fieldName: 'modality',
      errorText: 'Modality',
    },
    {
      fieldName: 'location',
      errorText: 'Location',
    },
    {
      fieldName: 'primary_condition',
      errorText: 'Primary condition',
    },
    {
      fieldName: 'other_primary_description',
      errorText: 'Other primary description',
    },
  ]

  let justRequired

  if ($$values.get('is_guaranteed_time')) {
    justRequired = requiredForGuaranteedTime.concat([
      {
        fieldName: 'guaranteed_time_session_type',
        errorText: 'Reason for Unused Session',
      },
    ])
    errors = Object.assign(errors, validation.requiredFields($$values, justRequired))
  } else {
    justRequired = requiredForGuaranteedTime.concat(restRequired)
    errors = Object.assign(errors, validation.requiredFields($$values, justRequired))
    errors = Object.assign(
      errors,
      validation.requiredValidName($$values, {
        fieldName: 'first_name',
        errorText: 'First name',
      }),
    )
    errors = Object.assign(
      errors,
      validation.requiredValidName($$values, {
        fieldName: 'last_name',
        errorText: 'Last name',
      }),
    )
    errors = Object.assign(
      errors,
      validation.requiredValidMinimumDOB($$values, {
        fieldName: 'eligible_member_dob',
        errorText: 'Eligible member Date of Birth',
      }),
    )
    errors = Object.assign(
      errors,
      validation.requiredValidCurrencyAmount($$values, {
        fieldName: 'session_rate',
        errorText: 'Session rate',
      }),
    )
  }

  errors = Object.assign(
    errors,
    validation.requiredValidInteger($$values, {
      fieldName: 'session_duration',
      errorText: 'Session length',
    }),
  )

  errors = Object.assign(
    errors,
    validation.validLyraCode(
      $$values,
      {
        fieldName: 'lyra_code',
        errorText: 'Lyra code',
      },
      get(props, 'selectedCompany[0].lyra_code_required') === 'true',
    ),
  )
  // This file is shared by RequestPayment.js and RequestPaymentAdmins.tsx
  // The OR assignments for $$formMeta, $$lyraCodeInfo, and $$user will be removed once ICD-10 collection changes are enabled for DA providers
  // https://lyrahealth.atlassian.net/browse/INTP-2653
  errors = Object.assign(
    errors,
    requestPaymentDOB({
      $$values,
      field: { fieldName: 'date_of_birth', errorText: 'Date of birth' },
      $$formMeta: props.$$formMeta || props.formMeta,
      lyraCodeInfo: props.lyraCodeValidationInfo,
      user: props.user,
      FORM_NAME: props.form,
      dispatch: props.dispatch,
    }),
  )

  errors = Object.assign(
    errors,
    validation.requiredBooleanField($$values, {
      fieldName: 'onsite_session',
      errorText: 'Onsite sesson',
    }),
  )
  errors = Object.assign(
    errors,
    validation.validVisitDate($$values, {
      fieldName: 'visit_date',
      errorText: 'Visit date',
    }),
  )

  if (isLeidos) {
    Object.keys(errors).forEach((fieldName) => {
      if (!LEIDOS_FORM_FIELDS.has(fieldName)) {
        delete (errors as Record<string, unknown>)[fieldName]
      }
    })
  }

  return errors
}

export const SESSION_LENGTH_TYPE_MISMATCH_WARNING =
  'Session length does not match session type. Verify before submitting.'

// Warning trigger rule for each DA meds follow-up complexity tier. Low only warns on
// over-long durations (a shorter session is still consistent with low complexity); high
// only warns on too-short; moderate warns on either side of the 30-39 window.
const DA_MEDS_COMPLEXITY_TIERS: Array<{
  sessionTypes: string[]
  isOutOfRange: (duration: number) => boolean
}> = [
  {
    sessionTypes: [
      'medication_management_follow_up_low_complexity',
      'minor_medication_management_follow_up_low_complexity',
    ],
    isOutOfRange: (d) => d > 29,
  },
  {
    sessionTypes: [
      'medication_management_follow_up_moderate_complexity',
      'minor_medication_management_follow_up_moderate_complexity',
    ],
    isOutOfRange: (d) => d < 30 || d > 39,
  },
  {
    sessionTypes: [
      'medication_management_follow_up_high_complexity',
      'minor_medication_management_follow_up_high_complexity',
    ],
    isOutOfRange: (d) => d < 40,
  },
]

export const getSessionLengthTypeMismatchWarning = (
  sessionType: string | undefined,
  duration: string | number | undefined,
): string | undefined => {
  if (!sessionType || duration === undefined || duration === null || duration === '') return undefined
  const parsed = typeof duration === 'string' ? parseInt(duration, 10) : duration
  // Skip negative durations — a separate validator surfaces those as errors.
  if (!Number.isFinite(parsed) || parsed < 0) return undefined
  const tier = DA_MEDS_COMPLEXITY_TIERS.find((t) => t.sessionTypes.includes(sessionType))
  if (tier?.isOutOfRange(parsed)) return SESSION_LENGTH_TYPE_MISMATCH_WARNING
  return undefined
}

export default requestPaymentValidate
